import { Box, Button, TextField, Typography } from '@mui/material'
import { useMutation, useQuery } from '@redwoodjs/web'
import { useState } from 'react'
import { verifyTOTP } from '../../../../api/src/lib/totp'
import { toast, Toaster } from '@redwoodjs/web/toast'
import { useAuth } from 'src/auth'
import { navigate, routes, useLocation } from '@redwoodjs/router'
import {
  canRoleUseLoginRedirect,
  getLoginRedirectFromSearch,
} from 'src/lib/auth/loginRedirect'
import { twoFactorAuth } from '../../../../api/src/services/totp/twoFactorAuth'

const GET_QR = gql`
  query GetTotpQR($username: String!) {
    getTotpQR(username: $username) {
      secret
      qr
    }
  }
`

const VERIFY_TOTP = gql`
  mutation VerifyTotp($secret: String!, $token: String!) {
    verifyTotp(secret: $secret, token: $token)
  }
`

export const TWO_FACTOR_AUTH = gql`
  mutation TwoFactorAuth($input: TwoFactorAuthInput!) {
    twoFactorAuth(input: $input) {
      id
      twoFactorVerified
    }
  }
`

const TwoFactorPage = () => {
  const { search } = useLocation()
  const [token, setToken] = useState<string>('')
  const [secret, setSecret] = useState<string>('')
  const [qr, setQr] = useState<string>('')

  const { currentUser, loading: userIsLoading, reauthenticate } = useAuth()
  const returnTo = getLoginRedirectFromSearch(search)

  const username = currentUser?.username
  const currentSecret = currentUser?.twoFactorSecret

  const { data, loading } = useQuery(GET_QR, {
    variables: {
      username,
    },
    onCompleted: (data) => {
      setSecret(data.getTotpQR.secret)
      setQr(data.getTotpQR.qr)
    },
  })

  const [verifyCode] = useMutation(VERIFY_TOTP, {
    onCompleted: (res) => {
      if (res.verifyTotp) handleAuth()
      else toast.error('Ogiltig kod')
    },
  })

  const [twoFactorAuth] = useMutation(TWO_FACTOR_AUTH)

  const handleAuth = async () => {
    try {
      const result = await twoFactorAuth({
        variables: {
          input: {
            twoFactorVerified: true,
            twoFactorSecret: currentSecret ?? secret,
          },
        },
      })

      if (result.data.twoFactorAuth.twoFactorVerified) {
        await reauthenticate()
        if (returnTo && canRoleUseLoginRedirect('admin', returnTo)) {
          navigate(returnTo)
          return
        }
        navigate(routes.adminWelcome())
      } else toast.error('Något gick fel med verifieringen')
    } catch (e) {
      toast.error('Något gick fel med verifieringen')
    }
  }

  if (!userIsLoading && !currentUser) {
    navigate(returnTo ? `${routes.login()}?returnTo=${encodeURIComponent(returnTo)}` : routes.login())
    return null
  } else if (!userIsLoading && currentUser?.twoFactorVerified) {
    if (returnTo && canRoleUseLoginRedirect('admin', returnTo)) {
      navigate(returnTo)
    } else {
      navigate(routes.adminWelcome())
    }
    return null
  }

  return (
    <>
      <Toaster toastOptions={{ className: 'rw-toast', duration: 3000 }} />

      <main>
        <style
          dangerouslySetInnerHTML={{
            __html: `
              html, body {
                margin: 0;
              }
              html * {
                box-sizing: border-box;
              }
              main {
                display: flex;
                align-items: center;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;
                text-align: center;
                background-color: #E2E8F0;
                height: 100vh;
              }
              section {
                background-color: white;
                border-radius: 0.25rem;
                width: 32rem;
                padding: 1.5rem;
                margin: 0 auto;
                box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
              }
            `,
          }}
        />
        <section>
          <Typography variant="h4">Authentication</Typography>

          {currentSecret ? (
            <Typography component="p" mt={2}>
              Ange engångskoden som tillhandahålls av{' '}
              <span style={{ fontWeight: 500 }}>
                Google Authenticator-appen
              </span>
              .
            </Typography>
          ) : (
            <>
              <img src={qr} alt="Scan QR" style={{ marginTop: 4 }} />

              <Typography component="p" mt={0}>
                Skanna QR-koden ovan med{' '}
                <span style={{ fontWeight: 500 }}>
                  Google Authenticator-appen
                </span>{' '}
                och ange sedan engångskoden som anges nedan.
              </Typography>
            </>
          )}

          <TextField
            id="outlined-basic"
            placeholder="XXXXXX"
            variant="outlined"
            sx={{
              mt: 2.5,
            }}
            value={token}
            onChange={(e) => {
              const input = e.target.value

              const sanitized = input.replace(/\D/g, '').slice(0, 6)
              setToken(sanitized)
            }}
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
          />

          <Box>
            <Button
              variant="contained"
              sx={{
                color: (t) => t.palette.common.white,
                mt: 2.5,
              }}
              onClick={() =>
                verifyCode({
                  variables: { secret: currentSecret ?? secret, token },
                })
              }
            >
              Fortsätt
            </Button>
          </Box>
        </section>
      </main>
    </>
  )
}

export default TwoFactorPage
